import { useQuery } from "@tanstack/react-query"
import { tintVTOService } from "services/tintvto"
import { useAlert } from "providers/AlertProvider"
import { useTranslation } from "react-i18next"
import { useAppSelector } from "hooks/storeHooks"
import { selectUserInfo } from "store/user/userSlice"
import { selectBillingEntitlement } from "store/billing/billingSlice"
import { IntegrationType } from "types"
import { getGenericUserErrorMessage } from "@/utils/userFacingErrors"

export const ANALYTICS_QUERY_KEY = (
  merchantId: string,
  days?: string,
  dateStart?: string,
  dateEnd?: string
) => [
  "analytics",
  {
    merchantId,
    days,
    dateStart,
    dateEnd,
  },
]

export type AnalyticsQueryParams = {
  merchantId: string
  days?: string
  dateStart?: string
  dateEnd?: string
}

export const useAnalyticsQuery = ({
  merchantId,
  days,
  dateStart,
  dateEnd,
}: AnalyticsQueryParams) => {
  const { showAlert } = useAlert()
  const { t } = useTranslation()
  const userInfo = useAppSelector(selectUserInfo)
  const billingEntitlement = useAppSelector(selectBillingEntitlement)

  const isAdmin = userInfo.groups?.some((group) => group.trim().toLowerCase() === "admin") || false
  const requiresBillingAccess = userInfo.integrationType === IntegrationType.COMMON && !isAdmin
  const canLoadAnalytics = !!merchantId && (!requiresBillingAccess || billingEntitlement?.entitled)

  return useQuery({
    enabled: canLoadAnalytics,
    queryKey: ANALYTICS_QUERY_KEY(merchantId, days, dateStart, dateEnd),
    queryFn: async () => {
      try {
        const { data } = await tintVTOService.getAnalytics(merchantId, days, dateStart, dateEnd)

        const analyticsString = data?.analytics
        if (typeof analyticsString === "string")
          try {
            const parsedData = JSON.parse(analyticsString)
            return parsedData
          } catch (parseError) {
            console.error("Failed to parse analytics JSON:", parseError)
            throw new Error("Invalid analytics data format")
          }

        return analyticsString
      } catch {
        showAlert({
          error: true,
          text: getGenericUserErrorMessage(t),
        })
        throw new Error(getGenericUserErrorMessage(t))
      }
    },
    retry: false,
  })
}
